import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from './Feedback';
import { Button } from './Button';
import { useT } from '../i18n/I18nProvider';

/**
 * Таблица данных.
 * columns: [{ key, header, render?(row), sortable?, sortValue?(row), align?: 'right'|'center', className?, hideOnMobile? }]
 * Сортировка — по загруженным строкам (серверная — через onSort/sort снаружи).
 */
export function DataTable({
  columns,
  rows,
  loading = false,
  rowKey = (row) => row.id,
  onRowClick,
  empty,
  pagination,
  className,
}) {
  const t = useT();
  const [sort, setSort] = useState(null); // { key, dir }

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column) return rows;
    const value = column.sortValue ?? ((row) => row[column.key]);
    return [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, columns, sort]);

  const toggleSort = (key) => {
    setSort((prev) => (!prev || prev.key !== key ? { key, dir: 'asc' } : prev.dir === 'asc' ? { key, dir: 'desc' } : null));
  };

  const alignClass = (align) => (align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left');

  return (
    <div className={clsx('overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card dark:border-zinc-800 dark:bg-zinc-900', className)}>
      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-full divide-y divide-zinc-200 text-sm dark:divide-zinc-800">
          <thead className="bg-zinc-50/80 dark:bg-zinc-900">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={clsx(
                    'whitespace-nowrap px-4 py-2.5 text-xs font-medium text-zinc-500 dark:text-zinc-400',
                    alignClass(col.align),
                    col.hideOnMobile && 'hidden md:table-cell'
                  )}
                  aria-sort={sort?.key === col.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {col.sortable ? (
                    <button type="button" onClick={() => toggleSort(col.key)} className="inline-flex items-center gap-1 hover:text-zinc-900 dark:hover:text-zinc-100">
                      {col.header}
                      {sort?.key === col.key ? (
                        sort.dir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`s-${i}`}>
                    {columns.map((col) => (
                      <td key={col.key} className={clsx('px-4 py-3', col.hideOnMobile && 'hidden md:table-cell')}>
                        <Skeleton className="h-4 w-full max-w-[10rem]" />
                      </td>
                    ))}
                  </tr>
                ))
              : sortedRows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={clsx(onRowClick && 'cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50', 'transition-colors')}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={clsx(
                          'whitespace-nowrap px-4 py-3 text-zinc-700 dark:text-zinc-300',
                          alignClass(col.align),
                          col.hideOnMobile && 'hidden md:table-cell',
                          col.className
                        )}
                      >
                        {col.render ? col.render(row) : row[col.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {!loading && rows.length === 0 && empty}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between border-t border-zinc-100 px-4 py-3 text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
          <span className="tabular">{t('common.pageOf', { page: pagination.page, pages: pagination.pages })}</span>
          <div className="flex gap-1">
            <Button variant="secondary" size="icon-sm" disabled={pagination.page <= 1} onClick={() => pagination.onPage(pagination.page - 1)} aria-label={t('common.previous')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="secondary" size="icon-sm" disabled={pagination.page >= pagination.pages} onClick={() => pagination.onPage(pagination.page + 1)} aria-label={t('common.next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
